import { useEffect, useRef, useState } from "react";
import { useClerk, useUser } from "@clerk/clerk-react";
import { ChevronDownIcon } from "./icons";

// Lightweight user menu — shows the user's Clerk avatar (or initials
// fallback) with a caret, opens a small dropdown with full name +
// sign-out. Replaces Clerk's UserButton so we don't expose the full
// user-account panel.
export function UserMenu() {
  const { user } = useUser();
  const clerk = useClerk();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!user) return null;

  const initials = initialsFromUser(user.firstName, user.lastName, user.username);
  const displayName =
    user.fullName ||
    user.username ||
    user.primaryEmailAddress?.emailAddress ||
    "Signed in";
  const email = user.primaryEmailAddress?.emailAddress ?? null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-full p-0.5 pr-2 hover:bg-slate-100 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar imageUrl={user.imageUrl} initials={initials} />
        <ChevronDownIcon className="text-slate-500" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 origin-top-right rounded-lg border border-slate-200 bg-white py-1 shadow-lg z-30"
        >
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-sm font-medium text-slate-900 truncate">
              {displayName}
            </p>
            {email && email !== displayName && (
              <p className="text-xs text-slate-500 truncate">{email}</p>
            )}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              setOpen(false);
              await clerk.signOut();
            }}
            className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Avatar({
  imageUrl,
  initials,
}: {
  imageUrl: string | null | undefined;
  initials: string;
}) {
  const [broken, setBroken] = useState(false);
  if (imageUrl && !broken) {
    return (
      <img
        src={imageUrl}
        alt=""
        width={32}
        height={32}
        className="h-8 w-8 rounded-full object-cover bg-slate-200"
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <div className="h-8 w-8 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">
      {initials}
    </div>
  );
}

function initialsFromUser(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  username: string | null | undefined,
): string {
  const a = firstName?.trim()[0];
  const b = lastName?.trim()[0];
  if (a && b) return (a + b).toUpperCase();
  if (a) return a.toUpperCase();
  if (username?.trim()[0]) return username.trim()[0]!.toUpperCase();
  return "·";
}
